
import { useState, useEffect } from "react";
import { useAddressBook } from "@/hooks/address/useAddressBook";
import { useAddressSelection } from "@/hooks/address/useAddressSelection";
import { useAddressActions } from "./address-selection/hooks/useAddressActions";
import { AddressSelectionHeader } from "./address-selection/components/AddressSelectionHeader";
import { AddressSelectionFields } from "./address-selection/components/AddressSelectionFields";
import { AddressActions } from "./address-selection/components/AddressActions";
import { GooglePlacesSelectValue } from "@/types/google-places";
import { AddressDetails } from "@/types/address";
import { useIsMobile } from "@/hooks/use-mobile";

interface AddressDetailsFormProps {
  values: AddressDetails;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  prefix: "collection" | "delivery";
  onPlaceSelect: (place: GooglePlacesSelectValue) => void;
  apiKey: string;
  showAddressFields: boolean;
}

export const AddressDetailsForm = ({
  values,
  onChange,
  onClear,
  prefix,
  onPlaceSelect,
  apiKey,
  showAddressFields,
}: AddressDetailsFormProps) => {
  const { savedAddresses, isLoading, loadSavedAddresses } = useAddressBook();
  const { showFields, setShowFields, handleAddressSelect, handleClear } = useAddressSelection({
    onChange,
    onPlaceSelect,
    onClear,
  });
  const { saveAddress } = useAddressActions();
  const isMobile = useIsMobile();

  useEffect(() => {
    setShowFields(showAddressFields);
  }, [showAddressFields, setShowFields]);

  const handleSaveAddress = async (label: string) => {
    await saveAddress({
      street: values.street,
      street2: values.street2,
      city: values.city,
      province: values.province,
      postal_code: values.postalCode,
      country: values.country,
      label: label || null,
    });
    
    loadSavedAddresses();
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className={`${isMobile ? 'space-y-6' : 'space-y-5'}`}>
      <AddressSelectionHeader
        apiKey={apiKey}
        savedAddresses={savedAddresses}
        onPlaceSelect={onPlaceSelect}
        onSavedAddressSelect={handleAddressSelect}
        prefix={prefix}
        currentAddress={values}
      />

      {showFields && (
        <>
          <AddressSelectionFields
            values={values}
            onChange={onChange}
            prefix={prefix}
          />
          <AddressActions
            onClear={handleClear}
            onSave={handleSaveAddress}
            prefix={prefix}
          />
        </>
      )}
    </div>
  );
};
